import { fullVersion } from '@/shbdeployer/product';
import { formatDateTimeSeconds, formatDuration } from '@/shbdeployer/format';

/**
 * Формирование безопасного диагностического блока для технической поддержки.
 * В блок не попадают пароли, токены, ключи, cookie и персональные данные —
 * используются только публичные сведения о продукте и данные из /api/system/diagnostics,
 * которые backend уже очистил от секретов.
 */
export function buildDiagnosticsReport({ product, status, diagnostics, browser }) {
  const lines = [];
  const now = new Date();
  lines.push(`=== ${fullVersion(product)} — диагностические сведения ===`);
  lines.push(`Сформировано: ${formatDateTimeSeconds(now)}`);
  lines.push('');
  lines.push('[Продукт]');
  lines.push(`Продукт: ${product.name}`);
  lines.push(`Разработчик: ${product.company}`);
  lines.push(`Версия: ${product.version || '—'}`);
  lines.push(`Сборка: ${product.buildNumber || '—'}`);
  lines.push(`Дата сборки: ${product.buildDate || '—'}`);
  lines.push(`Ревизия исходного кода: ${product.gitCommit || '—'}`);
  lines.push(`Окружение: ${product.environment || '—'}`);
  lines.push(`Идентификатор установки: ${product.instanceId || '—'}`);

  if (status) {
    lines.push('');
    lines.push('[Состояние компонентов]');
    lines.push(`Итог: ${status.overall}`);
    (status.components || []).forEach((c) => {
      const latency = c.latencyMs !== undefined && c.latencyMs !== null ? `, ${c.latencyMs} мс` : '';
      lines.push(`- ${c.name}: ${c.status}${latency}${c.details ? ` — ${c.details}` : ''}`);
    });
    if (status.uptimeSeconds !== undefined) {
      lines.push(`Время работы: ${formatDuration(status.uptimeSeconds)}`);
    }
  }

  if (diagnostics) {
    lines.push('');
    lines.push('[Сервер]');
    lines.push(`Запущен: ${diagnostics.startedAt || '—'}`);
    lines.push(`Версия схемы БД: ${diagnostics.dbVersion}`);
    if (diagnostics.runtime) {
      const r = diagnostics.runtime;
      lines.push(`Go: ${r.goVersion}, ${r.os}/${r.arch}, CPU: ${r.numCpu}, goroutines: ${r.goroutines}`);
      lines.push(`Память: heap ${r.heapAllocMb} МБ / ${r.heapSysMb} МБ, GC: ${r.numGc}`);
      lines.push(`Логи: формат ${r.logFormat}, уровень ${r.logLevel}; только HTTPS: ${r.httpsEnabled ? 'да' : 'нет'}`);
    }
    if (diagnostics.counters) {
      lines.push(
        'Объекты: ' +
          Object.entries(diagnostics.counters)
            .map(([k, v]) => `${k}=${v}`)
            .join(', ')
      );
    }
    if (diagnostics.features) {
      lines.push(
        'Функции: ' +
          Object.entries(diagnostics.features)
            .map(([k, v]) => `${k}=${v ? 'вкл' : 'выкл'}`)
            .join(', ')
      );
    }
  }

  if (browser) {
    lines.push('');
    lines.push('[Клиент]');
    lines.push(`Браузер: ${browser.userAgent}`);
    lines.push(`Язык: ${browser.language}`);
    lines.push(`Экран: ${browser.screen}`);
    lines.push(`Часовой пояс: ${browser.timeZone}`);
  }

  return lines.join('\n');
}

export function collectBrowserInfo() {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    screen: `${window.screen.width}×${window.screen.height}`,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

/** Копирует текст в буфер обмена с запасным вариантом для старых браузеров. */
export async function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}
